<template>
  <div class="auth-container">
    <div class="auth-background">
      <div class="grid-lines"></div>
      <div class="scan-line"></div>
    </div>
    
    <div class="auth-card">
      <div class="auth-header">
        <div class="logo-container">
          <svg width="48" height="48" viewBox="0 0 32 32" fill="none">
            <rect x="2" y="2" width="28" height="28" rx="6" stroke="var(--accent)" stroke-width="1.5" />
            <path d="M10 12L16 8L22 12V22L16 26L10 22V12Z" stroke="var(--accent)" stroke-width="1.5" />
            <circle cx="16" cy="16" r="3" fill="var(--accent)" opacity="0.6" />
          </svg>
          <h1 class="logo-text">MULTIFA<span class="accent">CED</span></h1>
        </div>
        <p class="subtitle">{{ currentSubtitle }}</p>
      </div>

      <!-- 标签切换 -->
      <div class="tabs-container">
        <button 
          v-for="tab in tabs" 
          :key="tab.id"
          class="tab-button"
          :class="{ active: activeTab === tab.id }"
          @click="switchTab(tab.id)"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- 登录表单 -->
      <form v-if="activeTab === 'login'" class="auth-form" @submit.prevent="handleLogin">
        <div class="form-group">
          <label class="form-label">
            <span class="label-text">账号</span>
            <span class="label-accent">ACCOUNT</span>
          </label>
          <div class="input-wrapper">
            <input 
              type="text" 
              v-model="loginForm.user"
              class="cyber-input"
              placeholder="请输入账号"
              @focus="focusInput('login', 'account')"
              @blur="blurInput('login', 'account')"
            />
            <div class="input-border" :class="{ active: focusStates.login.user }"></div>
            <div class="input-glow" :class="{ active: focusStates.login.user }"></div>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">
            <span class="label-text">密码</span>
            <span class="label-accent">PASSWORD</span>
          </label>
          <div class="input-wrapper">
            <input 
              :type="showLoginPassword ? 'text' : 'password'" 
              v-model="loginForm.pwd"
              class="cyber-input"
              placeholder="请输入密码"
              @focus="focusInput('login', 'password')"
              @blur="blurInput('login', 'password')"
            />
            <button 
              type="button" 
              class="password-toggle"
              @click="showLoginPassword = !showLoginPassword"
            >
              <svg v-if="showLoginPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
                <line x1="4.5" y1="4.5" x2="19.5" y2="19.5"></line>
              </svg>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </button>
            <div class="input-border" :class="{ active: focusStates.login.pwd }"></div>
            <div class="input-glow" :class="{ active: focusStates.login.pwd }"></div>
          </div>
        </div>

        <div class="form-options">
          <label class="remember-checkbox">
            <input type="checkbox" v-model="loginForm.remember" />
            <span class="checkmark"></span>
            <span class="check-text">记住我</span>
          </label>
        </div>

        <button type="submit" class="auth-button" :disabled="isLoading">
          <span v-if="!isLoading">
            <span class="button-text">登录</span>
            <span class="button-accent">LOGIN</span>
          </span>
          <span v-else class="loading-spinner">
            <span class="spinner-dot"></span>
            <span class="spinner-dot"></span>
            <span class="spinner-dot"></span>
          </span>
        </button>
      </form>

      <!-- 注册表单 -->
      <form v-else-if="activeTab === 'register'" class="auth-form" @submit.prevent="handleRegister">
        <div class="form-group">
          <label class="form-label">
            <span class="label-text">账号</span>
            <span class="label-accent">ACCOUNT</span>
          </label>
          <div class="input-wrapper">
            <input 
              type="text" 
              v-model="registerForm.user"
              class="cyber-input"
              placeholder="请输入账号"
              @focus="focusInput('register', 'user')"
              @blur="blurInput('register', 'user')"
            />
            <div class="input-border" :class="{ active: focusStates.register.user }"></div>
            <div class="input-glow" :class="{ active: focusStates.register.user }"></div>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">
            <span class="label-text">密码</span>
            <span class="label-accent">PASSWORD</span>
          </label>
          <div class="input-wrapper">
            <input 
              :type="showRegisterPassword ? 'text' : 'password'" 
              v-model="registerForm.pwd"
              class="cyber-input"
              placeholder="请输入密码"
              @focus="focusInput('register', 'password')"
              @blur="blurInput('register', 'password')"
            />
            <button 
              type="button" 
              class="password-toggle"
              @click="showRegisterPassword = !showRegisterPassword"
            >
              <svg v-if="showRegisterPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
                <line x1="4.5" y1="4.5" x2="19.5" y2="19.5"></line>
              </svg>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </button>
            <div class="input-border" :class="{ active: focusStates.register.pwd }"></div>
            <div class="input-glow" :class="{ active: focusStates.register.pwd }"></div>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">
            <span class="label-text">确认密码</span>
            <span class="label-accent">CONFIRM</span>
          </label>
          <div class="input-wrapper">
            <input 
              :type="showConfirmPassword ? 'text' : 'password'" 
              v-model="registerForm.cpwd"
              class="cyber-input"
              placeholder="请再次输入密码"
              @focus="focusInput('register', 'confirmPassword')"
              @blur="blurInput('register', 'confirmPassword')"
            />
            <button 
              type="button" 
              class="password-toggle"
              @click="showConfirmPassword = !showConfirmPassword"
            >
              <svg v-if="showConfirmPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
                <line x1="4.5" y1="4.5" x2="19.5" y2="19.5"></line>
              </svg>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </button>
            <div class="input-border" :class="{ active: focusStates.register.cpwd }"></div>
            <div class="input-glow" :class="{ active: focusStates.register.cpwd }"></div>
          </div>
        </div>



        <button type="submit" class="auth-button" :disabled="isLoading">
          <span v-if="!isLoading">
            <span class="button-text">注册</span>
            <span class="button-accent">REGISTER</span>
          </span>
          <span v-else class="loading-spinner">
            <span class="spinner-dot"></span>
            <span class="spinner-dot"></span>
            <span class="spinner-dot"></span>
          </span>
        </button>
      </form>

      <!-- 充值表单 -->
      <form v-else-if="activeTab === 'recharge'" class="auth-form" @submit.prevent="handleRecharge">
        <div class="form-group">
          <label class="form-label">
            <span class="label-text">账号</span>
            <span class="label-accent">ACCOUNT</span>
          </label>
          <div class="input-wrapper">
            <input 
              type="text" 
              v-model="rechargeForm.user"
              class="cyber-input"
              placeholder="请输入账号"
              @focus="focusInput('recharge', 'account')"
              @blur="blurInput('recharge', 'account')"
            />
            <div class="input-border" :class="{ active: focusStates.recharge.user }"></div>
            <div class="input-glow" :class="{ active: focusStates.recharge.user }"></div>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">
            <span class="label-text">卡密</span>
            <span class="label-accent">CARD CODE</span>
          </label>
          <div class="input-wrapper">
            <input 
              type="text" 
              v-model="rechargeForm.key"
              class="cyber-input"
              placeholder="请输入卡密"
              @focus="focusInput('recharge', 'cardCode')"
              @blur="blurInput('recharge', 'cardCode')"
            />
            <div class="input-border" :class="{ active: focusStates.recharge.key }"></div>
            <div class="input-glow" :class="{ active: focusStates.recharge.key }"></div>
          </div>
        </div>

        <div class="form-tips">
          <div class="tip-item">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="7" cy="7" r="5"></circle>
              <circle cx="7" cy="5" r="0.5" fill="currentColor"></circle>
              <path d="M5 10L7 7L9 10" stroke-linecap="round"></path>
            </svg>
            <span>请输入正确的账号和卡密进行充值</span>
          </div>
        </div>

        <button type="submit" class="auth-button" :disabled="isLoading">
          <span v-if="!isLoading">
            <span class="button-text">充值</span>
            <span class="button-accent">RECHARGE</span>
          </span>
          <span v-else class="loading-spinner">
            <span class="spinner-dot"></span>
            <span class="spinner-dot"></span>
            <span class="spinner-dot"></span>
          </span>
        </button>
      </form>

      <div class="auth-footer">
        <p class="footer-text" v-if="activeTab === 'login'">
          还没有账号？ <a  class="footer-link" @click.prevent="switchTab('register')">立即注册</a>
        </p>
        <p class="footer-text" v-else-if="activeTab === 'register'">
          已有账号？ <a class="footer-link" @click.prevent="switchTab('login')">立即登录</a>
        </p>
        <p class="footer-text" v-else-if="activeTab === 'recharge'">
          <a  class="footer-link" @click.prevent="switchTab('login')">返回登录</a>
        </p>
      </div>

      <div class="decorative-corner top-left"></div>
      <div class="decorative-corner top-right"></div>
      <div class="decorative-corner bottom-left"></div>
      <div class="decorative-corner bottom-right"></div>
    </div>

    <div class="terminal-text">
      <span>> SYSTEM READY</span>
      <span class="blinking-cursor">_</span>
    </div>
    
    <CyberModal 
      :visible="modalVisible" 
      :title="modalTitle" 
      :message="modalMessage" 
      @close="modalVisible = false" 
      @confirm="handleModalConfirm" 
    />
  </div>
</template>

<script>
import CyberModal from '@/components/CyberModal.vue'
import http from "@/utils/http";

export default {
  name: 'AuthPage',
  inject:["getUserInfo","eSend"],
  components: {
    CyberModal
  },
  data() {
    return {
      activeTab: 'login',
      tabs: [
        { id: 'login', label: '登录' },
        { id: 'register', label: '注册' },
        { id: 'recharge', label: '充值' }
      ],
      showLoginPassword: false,
      showRegisterPassword: false,
      showConfirmPassword: false,
      isLoading: false,
      focusStates: {
        login: { user: false, pwd: false },
        register: { user: false, pwd: false, cpwd: false },
        recharge: { user: false, key: false }
      },
      loginForm: {
        user: '',
        pwd: '',
        remember: false
      },
      registerForm: {
        user: '',
        pwd: '',
        cpwd: '',

      },
      rechargeForm: {
        user: '',
        key: ''
      },
      modalVisible: false,
      modalTitle: '系统提示',
      modalMessage: '',
      redirectAfterConfirm: null
    }
  },
  computed: {
    currentSubtitle() {
      const subtitles = {
        login: '专业音频视频去重平台',
        register: '创建您的账号',
        recharge: '充值卡密'
      }
      return subtitles[this.activeTab]
    }
  },
  watch: {
    '$route'(to) {
      this.updateTabFromRoute(to.path)
    }
  },
  activated() {


  },
  mounted() {


    this.updateTabFromRoute(this.$route.path)


    const loginForm=localStorage.getItem("loginForm");

    if(loginForm){

      this.loginForm=JSON.parse(loginForm);

    }


  },
  methods: {
    updateTabFromRoute(path) {
      if (path === '/register') {
        this.activeTab = 'register'
      } else if (path === '/recharge') {
        this.activeTab = 'recharge'
      } else {
        this.activeTab = 'login'
      }
    },
    switchTab(tabId) {
      this.activeTab = tabId
      // 同步路由
      if (tabId === 'login') {
        this.$router.push('/login')
      } else if (tabId === 'register') {
        this.$router.push('/register')
      } else if (tabId === 'recharge') {
        this.$router.push('/recharge')
      }
    },
    focusInput(tab, field) {
      this.focusStates[tab][field] = true
    },
    blurInput(tab, field) {
      this.focusStates[tab][field] = false
    },
    async handleLogin() {
      if (!this.loginForm.user || !this.loginForm.pwd) {
        this.showModal('输入提示', '请填写账号和密码')
        return
      }

      this.isLoading = true

      const res=await http.post("api/user/login",this.loginForm);
      this.isLoading = false


      if(res.code==200){
        this.$store.commit("setToken",res.data);

        localStorage.setItem("loginForm",JSON.stringify(this.loginForm));

        this.getUserInfo();
        this.eSend("start",res.data);
        this.showModal('登录成功', '欢迎回来！即将跳转到首页', () => {
          this.$router.push('/')
        })
        return;
      }

      this.showModal('登录提示', res.msg);



    },
    async handleRegister() {
      if (!this.registerForm.user || !this.registerForm.pwd || !this.registerForm.cpwd) {
        this.showModal('输入提示', '请填写完整信息')
        return
      }
      
      if (this.registerForm.pwd !== this.registerForm.cpwd) {
        this.showModal('密码错误', '两次输入的密码不一致')
        return
      }
      


      this.isLoading = true

      const res=await http.post("api/user/reg",this.registerForm);
      this.isLoading = false

      if(res.code==200){
        this.showModal('注册成功', res.msg, () => {
          this.switchTab('login')

        })
        this.registerForm = {
          user: '',
          pwd: '',
          cpwd: '',
        }

        return;
      }
      this.showModal('注册失败', res.msg)


    },
    async handleRecharge() {
      if (!this.rechargeForm.user || !this.rechargeForm.key) {
        this.showModal('输入提示', '请填写账号和卡密')
        return
      }

      this.isLoading = true

      const res=await http.post("api/user/recharge",this.rechargeForm);
      this.isLoading = false

      if(res.code==200){
        this.showModal('充值成功', res.msg)
        this.rechargeForm = {
          user: '',
          key: ''
        }
        return;
      }
      this.showModal('充值失败', res.msg)

    },
    showModal(title, message, callback = null) {
      this.modalTitle = title
      this.modalMessage = message
      this.redirectAfterConfirm = callback
      this.modalVisible = true
    },
    handleModalConfirm() {
      if (this.redirectAfterConfirm) {
        this.redirectAfterConfirm()
      }
    }
  }
}
</script>

<style scoped>
.auth-container {
  min-height: 100vh;
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: var(--bg-primary);
  padding: 20px;
  box-sizing: border-box;
}

.auth-background {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.grid-lines {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(0, 212, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 212, 255, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  animation: gridMove 20s linear infinite;
}

@keyframes gridMove {
  0% { transform: perspective(500px) rotateX(60deg) translateY(0); }
  100% { transform: perspective(500px) rotateX(60deg) translateY(40px); }
}

.scan-line {
  position: absolute;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--accent), transparent);
  opacity: 0.6;
  animation: scanLine 3s linear infinite;
}

@keyframes scanLine {
  0% { top: 0; }
  100% { top: 100%; }
}

.auth-card {
  position: relative;
  width: 100%;
  max-width: 560px;
  padding: 48px 60px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: 
    0 0 40px rgba(0, 212, 255, 0.1),
    inset 0 0 40px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  z-index: 1;
}

.auth-header {
  text-align: center;
  margin-bottom: 32px;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 12px;
}

.logo-text {
  font-family: 'Orbitron', sans-serif;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: 2px;
  color: var(--text-primary);
}

.logo-text .accent {
  color: var(--accent);
  text-shadow: 0 0 20px var(--accent-glow);
}

.subtitle {
  font-size: 13px;
  color: var(--text-secondary);
  letter-spacing: 2px;
  text-transform: uppercase;
  margin: 0;
}

.tabs-container {
  display: flex;
  gap: 4px;
  margin-bottom: 32px;
  padding: 4px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
}

.tab-button {
  flex: 1;
  padding: 12px 16px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--text-secondary);
  font-family: 'Orbitron', sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-button:hover {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.05);
}

.tab-button.active {
  background: linear-gradient(135deg, var(--accent), #0099cc);
  color: #000;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.label-text {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 500;
}

.label-accent {
  font-family: 'Orbitron', sans-serif;
  font-size: 10px;
  color: var(--accent);
  opacity: 0.6;
  letter-spacing: 1px;
}

.input-wrapper {
  position: relative;
}

.cyber-input {
  width: 100%;
  padding: 14px 16px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  font-family: 'JetBrains Mono', monospace;
  outline: none;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
  box-sizing: border-box;
}

.cyber-input::placeholder {
  color: var(--text-muted);
}

.cyber-input:focus {
  border-color: transparent;
  background: rgba(0, 212, 255, 0.05);
}

.input-border {
  position: absolute;
  inset: 0;
  border: 1px solid var(--accent);
  border-radius: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.input-border.active {
  opacity: 1;
  animation: borderPulse 2s ease-in-out infinite;
}

@keyframes borderPulse {
  0%, 100% { box-shadow: 0 0 10px var(--accent-dim); }
  50% { box-shadow: 0 0 20px var(--accent-glow); }
}

.input-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, rgba(0, 212, 255, 0.1), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 8px;
  pointer-events: none;
}

.input-glow.active {
  opacity: 1;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  z-index: 3;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.password-toggle:hover {
  color: var(--accent);
}

.form-options {
  margin-top: 4px;
}

.remember-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.remember-checkbox input {
  display: none;
}

.checkmark {
  width: 18px;
  height: 18px;
  border: 1px solid var(--border);
  border-radius: 4px;
  position: relative;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.remember-checkbox input:checked + .checkmark {
  background: var(--accent);
  border-color: var(--accent);
  box-shadow: 0 0 10px var(--accent-glow);
}

.remember-checkbox input:checked + .checkmark::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 2px;
  width: 4px;
  height: 8px;
  border: solid #000;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.check-text {
  font-size: 13px;
  color: var(--text-secondary);
}

.terms-link {
  color: var(--accent);
  text-decoration: none;
}

.form-tips {
  margin-top: 4px;
}

.tip-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 12px;
  padding: 10px 14px;
  background: rgba(0, 212, 255, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(0, 212, 255, 0.1);
}

.tip-item svg {
  color: var(--accent);
  flex-shrink: 0;
}

.auth-button {
  width: 100%;
  padding: 16px 32px;
  background: linear-gradient(135deg, var(--accent), #0099cc);
  border: none;
  border-radius: 8px;
  color: #000;
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 2px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  margin-top: 8px;
}

.auth-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.auth-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.auth-button:hover::before {
  left: 100%;
}

.auth-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px var(--accent-glow);
}

.button-text {
  font-size: 14px;
}

.button-accent {
  font-size: 10px;
  opacity: 0.8;
}

.loading-spinner {
  display: flex;
  gap: 4px;
}

.spinner-dot {
  width: 8px;
  height: 8px;
  background: #000;
  border-radius: 50%;
  animation: dotPulse 1.4s ease-in-out infinite;
}

.spinner-dot:nth-child(1) { animation-delay: 0s; }
.spinner-dot:nth-child(2) { animation-delay: 0.2s; }
.spinner-dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes dotPulse {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

.auth-footer {
  margin-top: 28px;
  text-align: center;
}

.footer-text {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.footer-link {
  color: var(--accent);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
}

.footer-link:hover {
  text-shadow: 0 0 10px var(--accent-glow);
}

.decorative-corner {
  position: absolute;
  width: 20px;
  height: 20px;
}

.decorative-corner::before,
.decorative-corner::after {
  content: '';
  position: absolute;
  background: var(--accent);
  opacity: 0.6;
}

.decorative-corner.top-left {
  top: 8px;
  left: 8px;
}
.decorative-corner.top-left::before {
  top: 0;
  left: 0;
  width: 2px;
  height: 16px;
}
.decorative-corner.top-left::after {
  top: 0;
  left: 0;
  width: 16px;
  height: 2px;
}

.decorative-corner.top-right {
  top: 8px;
  right: 8px;
}
.decorative-corner.top-right::before {
  top: 0;
  right: 0;
  width: 2px;
  height: 16px;
}
.decorative-corner.top-right::after {
  top: 0;
  right: 0;
  width: 16px;
  height: 2px;
}

.decorative-corner.bottom-left {
  bottom: 8px;
  left: 8px;
}
.decorative-corner.bottom-left::before {
  bottom: 0;
  left: 0;
  width: 2px;
  height: 16px;
}
.decorative-corner.bottom-left::after {
  bottom: 0;
  left: 0;
  width: 16px;
  height: 2px;
}

.decorative-corner.bottom-right {
  bottom: 8px;
  right: 8px;
}
.decorative-corner.bottom-right::before {
  bottom: 0;
  right: 0;
  width: 2px;
  height: 16px;
}
.decorative-corner.bottom-right::after {
  bottom: 0;
  right: 0;
  width: 16px;
  height: 2px;
}

.terminal-text {
  position: absolute;
  bottom: 24px;
  left: 24px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: var(--accent);
  opacity: 0.5;
  letter-spacing: 2px;
}

.blinking-cursor {
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
</style>